import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  LabeledValue,
  ListHeader,
  ModalActions,
  ModalContent,
  ModalOpenButton,
  ModalTitle,
  ModalWithSidePanelDialog,
  ModalWithSidePanelSections,
} from "@msys/ui";
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { SelectedBoxWithIndicator } from "../../../commons/SelectedBoxWithIndicator.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import { useOrganisationRoles } from "../../users/useRoles.js";
import {
  OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment,
  useAvailableTriggerEventNamesQuery,
  useCreateOrganisationWorkflowMutation,
  useModifyOrganisationWorkflowSettingsMutation,
  useWorkflowTypeDefinitionsQuery,
  WorkflowTypeDefinitionsQuery,
} from "../boxes/OrganisationAutomatedWorkflowsBox.generated.js";
import { useAddOrganisationEmailTemplateMutation } from "../boxes/OrganisationEmailSettingsBox.generated.js";
import { useEmailTemplateContexts } from "../boxes/useEmailTemplateContexts.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
} from "../OrganisationMemberSelect.generated.js";
import { OrganisationEmailTemplateModal } from "./OrganisationEmailTemplateModal.js";

type ConfigField =
  WorkflowTypeDefinitionsQuery["workflowTypeDefinitions"][number]["configFields"][number];

const SOURCE_TYPES = [
  "Manual",
  "FlowWizard",
  "Flow3D",
  "FlowVoiceAssistant",
  "FlowM1",
  "RequestManual",
  "RequestAutomated",
  "Import",
  "QuoteProductsDistribution",
  "ContracteeInvitation",
];

type SidePanelState =
  | { status: "closed" }
  | { status: "email-template-select"; configKey: string }
  | { status: "email-template-create"; configKey: string }
  | { status: "source-type-select" }
  | { status: "recipient-select"; configKey: string }
  | { status: "trigger-event-select" };

interface FormValues {
  workflowType: string;
  description: string;
  triggerEventNames: string[];
  sourceTypeFilter: string[];
  config: Record<string, unknown>;
}

interface Props {
  workflow?: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment;
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  organisationId?: string;
  handleClose: () => void;
}

export const OrganisationWorkflowPanel = ({
  workflow,
  emailTemplates,
  organisationId,
  handleClose,
}: Props) => {
  const isEditMode = !!workflow;
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const formId = React.useMemo(() => uniqueId(), []);

  const [sidePanelState, setSidePanelState] = React.useState<SidePanelState>({
    status: "closed",
  });
  const showSideContent = sidePanelState.status !== "closed";

  const handleCloseSidePanel = React.useCallback(() => {
    setSidePanelState({ status: "closed" });
  }, []);

  // Queries
  const { data: typeDefsData } = useWorkflowTypeDefinitionsQuery({ client });
  const { data: triggerEventNamesData } = useAvailableTriggerEventNamesQuery({
    client,
  });

  const workflowTypeDefs = typeDefsData?.workflowTypeDefinitions ?? [];

  // Mutations
  const [createWorkflow] = useCreateOrganisationWorkflowMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationWorkflows,
      namedOperations.Query.OrganisationSettingsIntegrations,
    ],
  });

  const [modifyWorkflowSettings] =
    useModifyOrganisationWorkflowSettingsMutation({ client });

  const [addEmailTemplate] = useAddOrganisationEmailTemplateMutation({
    client,
    refetchQueries: [namedOperations.Query.OrganisationSettingsIntegrations],
  });

  // Options
  const workflowTypeOptions = React.useMemo(
    () =>
      workflowTypeDefs.map(def => ({
        value: def.workflowType,
        label: t(def.label as any, { ns: "OrganisationSettings" }),
      })),
    [t, workflowTypeDefs]
  );

  const getTypeDef = React.useCallback(
    (workflowType: string) =>
      workflowTypeDefs.find(d => d.workflowType === workflowType),
    [workflowTypeDefs]
  );

  const initialValues: FormValues = React.useMemo(() => {
    if (isEditMode) {
      const config = (workflow.config as Record<string, unknown>) ?? {};
      const triggerFilter = workflow.triggerFilter as Record<
        string,
        string[]
      > | null;
      return {
        workflowType: workflow.workflowType,
        description: workflow.description,
        triggerEventNames: workflow.triggerEventNames ?? [],
        sourceTypeFilter: triggerFilter?.["payload.sourceType"] ?? [],
        config,
      };
    }
    return {
      workflowType: "",
      description: "",
      triggerEventNames: [],
      sourceTypeFilter: [],
      config: {},
    };
  }, [isEditMode, workflow]);

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      try {
        const triggerFilter =
          values.sourceTypeFilter.length > 0
            ? { "payload.sourceType": values.sourceTypeFilter }
            : {};

        if (isEditMode) {
          await modifyWorkflowSettings({
            variables: {
              workflowId: workflow.id,
              input: {
                description: values.description,
                triggerEventNames: values.triggerEventNames,
                triggerFilter,
                config: values.config,
              },
            },
          });
        } else {
          await createWorkflow({
            variables: {
              input: {
                workflowType: values.workflowType,
                description: values.description,
                triggerEventNames: values.triggerEventNames,
                triggerFilter,
                config: values.config,
              },
            },
          });
        }
        handleClose();
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [
      isEditMode,
      workflow,
      createWorkflow,
      modifyWorkflowSettings,
      handleClose,
      enqueueSnackbar,
    ]
  );

  return (
    <ModalWithSidePanelDialog
      fixedHeight={showSideContent}
      dialogProps={{
        maxWidth: showSideContent ? "lg" : "sm",
      }}
      handleClose={handleClose}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {formikProps => {
          const typeDef = getTypeDef(formikProps.values.workflowType);
          const configFields = typeDef?.configFields ?? [];
          const allRequiredConfigured = configFields
            .filter(f => f.required)
            .every(f => !!formikProps.values.config[f.key]);

          return (
            <ModalWithSidePanelSections
              sideContent={
                sidePanelState.status === "email-template-select" ||
                sidePanelState.status === "email-template-create" ? (
                  <EmailTemplateSidePanel
                    emailTemplates={emailTemplates}
                    handleClose={handleCloseSidePanel}
                    addEmailTemplate={addEmailTemplate}
                    configKey={sidePanelState.configKey}
                  />
                ) : sidePanelState.status === "source-type-select" ? (
                  <SourceTypeSelectPanel handleClose={handleCloseSidePanel} />
                ) : sidePanelState.status === "recipient-select" ? (
                  <RecipientSelectSidePanel
                    handleClose={handleCloseSidePanel}
                    configKey={sidePanelState.configKey}
                  />
                ) : sidePanelState.status === "trigger-event-select" ? (
                  <TriggerEventSelectPanel
                    handleClose={handleCloseSidePanel}
                    availableEvents={
                      triggerEventNamesData?.availableTriggerEventNames ?? []
                    }
                    compatibleEvents={typeDef?.compatibleTriggerEvents ?? []}
                  />
                ) : sidePanelState.status === "closed" ? null : (
                  assertNever(sidePanelState)
                )
              }
              maxWidth="sm"
              maxWidthWithSideContent="lg"
            >
              <ModalTitle
                title={
                  isEditMode
                    ? t("Edit workflow" as any, { ns: "OrganisationSettings" })
                    : t("Create workflow" as any, {
                        ns: "OrganisationSettings",
                      })
                }
                handleClose={handleClose}
              />
              <ModalContent>
                <Form id={formId}>
                  <Stack spacing={2}>
                    {!isEditMode && (
                      <WorkflowTypeWatcher
                        workflowTypeDefs={workflowTypeDefs}
                      />
                    )}

                    {/* Workflow type */}
                    {isEditMode ? (
                      <LabeledValue
                        label={t("Workflow type" as any, {
                          ns: "OrganisationSettings",
                        })}
                      >
                        {t((typeDef?.label ?? workflow.workflowType) as any, {
                          ns: "OrganisationSettings",
                        })}
                      </LabeledValue>
                    ) : (
                      <>
                        <SelectField
                          name="workflowType"
                          label={t("Workflow type" as any, {
                            ns: "OrganisationSettings",
                          })}
                          options={workflowTypeOptions}
                        />
                        {typeDef && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: -1 }}
                          >
                            {t(typeDef.description as any, {
                              ns: "OrganisationSettings",
                            })}
                          </Typography>
                        )}
                      </>
                    )}

                    {/* Show remaining fields only after type selection */}
                    {(isEditMode || formikProps.values.workflowType) && (
                      <>
                        {/* Description */}
                        <TextField
                          name="description"
                          label={t("Description", {
                            ns: "OrganisationSettings",
                          })}
                          required={!isEditMode}
                        />

                        {/* Trigger events */}
                        <Stack spacing={0.5}>
                          <Box width="100%">
                            {formikProps.values.triggerEventNames.length > 0 ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <LabeledValue
                                  label={t("Trigger events" as any, {
                                    ns: "OrganisationSettings",
                                  })}
                                >
                                  {formikProps.values.triggerEventNames.join(
                                    ", "
                                  )}
                                </LabeledValue>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    setSidePanelState({
                                      status: "trigger-event-select",
                                    })
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              </Stack>
                            ) : (
                              <SelectedBoxWithIndicator
                                selected={
                                  sidePanelState.status ===
                                  "trigger-event-select"
                                }
                              >
                                <Button
                                  size="small"
                                  color="secondary"
                                  variant="text"
                                  startIcon={<AddIcon />}
                                  sx={{ textWrap: "nowrap" }}
                                  onClick={() =>
                                    setSidePanelState({
                                      status: "trigger-event-select",
                                    })
                                  }
                                >
                                  {t("Select trigger events" as any, {
                                    ns: "OrganisationSettings",
                                  })}
                                </Button>
                              </SelectedBoxWithIndicator>
                            )}
                          </Box>
                        </Stack>

                        {/* Dynamic config fields */}
                        {configFields.map(field => (
                          <DynamicConfigField
                            key={field.key}
                            field={field}
                            emailTemplates={emailTemplates}
                            sidePanelState={sidePanelState}
                            setSidePanelState={setSidePanelState}
                          />
                        ))}

                        {/* Source type filter */}
                        <Stack spacing={0.5}>
                          <Box width="100%">
                            {formikProps.values.sourceTypeFilter.length > 0 ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <LabeledValue
                                  label={t("Source type filter" as any, {
                                    ns: "OrganisationSettings",
                                  })}
                                >
                                  {formikProps.values.sourceTypeFilter.join(
                                    ", "
                                  )}
                                </LabeledValue>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    setSidePanelState({
                                      status: "source-type-select",
                                    })
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              </Stack>
                            ) : (
                              <SelectedBoxWithIndicator
                                selected={
                                  sidePanelState.status === "source-type-select"
                                }
                              >
                                <Button
                                  size="small"
                                  color="secondary"
                                  variant="text"
                                  startIcon={<AddIcon />}
                                  sx={{ textWrap: "nowrap" }}
                                  onClick={() =>
                                    setSidePanelState({
                                      status: "source-type-select",
                                    })
                                  }
                                >
                                  {t("Select source types" as any, {
                                    ns: "OrganisationSettings",
                                  })}
                                </Button>
                              </SelectedBoxWithIndicator>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ pl: 0.5 }}
                          >
                            {t(
                              "Filter which quote sources trigger this workflow. Leave empty for all sources." as any,
                              { ns: "OrganisationSettings" }
                            )}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Form>
              </ModalContent>
              <ModalActions
                actionButtons={[
                  {
                    label: t("Cancel", { ns: "Global" }),
                    handleClick: handleClose,
                    buttonProps: {
                      variant: "text",
                      disabled: formikProps.isSubmitting,
                    },
                  },
                  {
                    label: isEditMode
                      ? t("Save", { ns: "Global" })
                      : t("Create", { ns: "Global" }),
                    buttonProps: {
                      type: "submit",
                      form: formId,
                      disabled:
                        !allRequiredConfigured ||
                        formikProps.values.triggerEventNames.length === 0 ||
                        (!isEditMode &&
                          (!formikProps.values.workflowType ||
                            !formikProps.values.description)),
                      loading: formikProps.isSubmitting,
                    },
                  },
                ]}
              />
            </ModalWithSidePanelSections>
          );
        }}
      </Formik>
    </ModalWithSidePanelDialog>
  );
};

// --- Dynamic Config Field ---

function DynamicConfigField({
  field,
  emailTemplates,
  sidePanelState,
  setSidePanelState,
}: {
  field: ConfigField;
  emailTemplates: Props["emailTemplates"];
  sidePanelState: SidePanelState;
  setSidePanelState: (state: SidePanelState) => void;
}) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const configValue = values.config[field.key];

  switch (field.fieldType) {
    case "emailTemplatePicker": {
      const selectedId = configValue as string | null;
      return (
        <Box width="100%">
          {selectedId ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabeledValue
                label={t(field.label as any, { ns: "OrganisationSettings" })}
              >
                {emailTemplates.find(et => et.id === selectedId)?.description ??
                  selectedId}
              </LabeledValue>
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  setFieldValue("config", {
                    ...values.config,
                    [field.key]: null,
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <SelectedBoxWithIndicator
              selected={
                (sidePanelState.status === "email-template-select" ||
                  sidePanelState.status === "email-template-create") &&
                sidePanelState.configKey === field.key
              }
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "email-template-select",
                    configKey: field.key,
                  })
                }
              >
                {t("Select email template" as any, {
                  ns: "OrganisationSettings",
                })}
              </Button>
            </SelectedBoxWithIndicator>
          )}
        </Box>
      );
    }
    case "memberPicker": {
      const selectedId = configValue as string | null;
      return (
        <Box width="100%">
          {selectedId ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabeledValue
                label={t(field.label as any, { ns: "OrganisationSettings" })}
              >
                <MemberButtonLabel memberId={selectedId} />
              </LabeledValue>
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  setFieldValue("config", {
                    ...values.config,
                    [field.key]: null,
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <SelectedBoxWithIndicator
              selected={
                sidePanelState.status === "recipient-select" &&
                sidePanelState.configKey === field.key
              }
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "recipient-select",
                    configKey: field.key,
                  })
                }
              >
                {t("Select recipient" as any, {
                  ns: "OrganisationSettings",
                })}
              </Button>
            </SelectedBoxWithIndicator>
          )}
        </Box>
      );
    }
    case "text":
      return <ConfigTextField configKey={field.key} label={field.label} />;
    case "number":
      return (
        <ConfigTextField
          configKey={field.key}
          label={field.label}
          type="number"
        />
      );
    case "boolean": {
      const checked = !!configValue;
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(_e, newChecked) => {
                setFieldValue("config", {
                  ...values.config,
                  [field.key]: newChecked,
                });
              }}
              size="small"
            />
          }
          label={t(field.label as any, { ns: "OrganisationSettings" })}
        />
      );
    }
    default:
      return <ConfigTextField configKey={field.key} label={field.label} />;
  }
}

function ConfigTextField({
  configKey,
  label,
  type,
}: {
  configKey: string;
  label: string;
  type?: string;
}) {
  const { t } = useTranslate(["OrganisationSettings"]);

  return (
    <TextField
      name={`config.${configKey}`}
      label={t(label as any, { ns: "OrganisationSettings" })}
      type={type}
    />
  );
}

// --- Helper Components ---

function MemberButtonLabel({ memberId }: { memberId: string }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const client = useApolloClient();

  const selectedQuery = useOrganisationMemberSelect_SelectedMembersQuery({
    client,
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      selectedMembers: [memberId],
      limit: 1,
    },
  });

  const selectedMember = selectedQuery.data?.selectedMembers.edges
    .map(e => e.node)
    .find(m => m.id === memberId);

  if (selectedMember) {
    return <>{selectedMember.fullname}</>;
  }

  return (
    <>
      {t("Select recipient" as any, {
        ns: "OrganisationSettings",
      })}
    </>
  );
}

function WorkflowTypeWatcher({
  workflowTypeDefs,
}: {
  workflowTypeDefs: WorkflowTypeDefinitionsQuery["workflowTypeDefinitions"];
}) {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const prevRef = React.useRef(values.workflowType);

  React.useEffect(() => {
    if (values.workflowType && values.workflowType !== prevRef.current) {
      prevRef.current = values.workflowType;
      const typeDef = workflowTypeDefs.find(
        d => d.workflowType === values.workflowType
      );
      setFieldValue("sourceTypeFilter", []);
      setFieldValue("config", {});
      setFieldValue(
        "triggerEventNames",
        typeDef?.compatibleTriggerEvents ?? []
      );
    }
  }, [values.workflowType, setFieldValue, workflowTypeDefs]);

  return null;
}

// --- Side Panels ---

function EmailTemplateSidePanel({
  emailTemplates,
  handleClose,
  addEmailTemplate,
  configKey,
}: {
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  handleClose: () => void;
  addEmailTemplate: ReturnType<
    typeof useAddOrganisationEmailTemplateMutation
  >[0];
  configKey: string;
}) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { labels: emailTemplateContextLabels } = useEmailTemplateContexts();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const filteredTemplates = React.useMemo(() => {
    if (!searchTerm) return emailTemplates;
    const lower = searchTerm.toLowerCase();
    return emailTemplates.filter(
      tpl =>
        tpl.description.toLowerCase().includes(lower) ||
        (
          emailTemplateContextLabels[
            tpl.context as keyof typeof emailTemplateContextLabels
          ] ?? ""
        )
          .toLowerCase()
          .includes(lower)
    );
  }, [emailTemplates, searchTerm, emailTemplateContextLabels]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "description",
        headerName: t("Template", { ns: "OrganisationSettings" }),
        flex: 1,
        sortable: false,
      },
      {
        field: "context",
        headerName: t("Context", { ns: "OrganisationSettings" }),
        sortable: false,
        valueGetter: (value: string) =>
          emailTemplateContextLabels[
            value as keyof typeof emailTemplateContextLabels
          ] ?? value,
      },
      {
        field: "attachmentCount",
        flex: 0,
        maxWidth: 50,
        align: "right" as const,
        sortable: false,
        renderHeader: () => <AttachFileIcon />,
        headerAlign: "right" as const,
        valueGetter: (_value: unknown, row: (typeof emailTemplates)[0]) =>
          row.attachments.length,
      },
    ],
    [t, emailTemplateContextLabels]
  );

  const headerActions = (
    <ModalOpenButton
      Modal={OrganisationEmailTemplateModal}
      modalProps={{
        title: t("New email template", { ns: "OrganisationSettings" }),
        handleComplete: async (values: any, modalHandleClose: () => void) => {
          await addEmailTemplate({ variables: { input: values } });
          modalHandleClose();
        },
      }}
    >
      <ButtonCreate
        variant="contained"
        color="primary"
        title={t("Create new" as any, { ns: "Global" })}
      />
    </ModalOpenButton>
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select email template" as any, {
          ns: "OrganisationSettings",
        })}
        headerActions={headerActions}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={filteredTemplates}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("config", {
              ...values.config,
              [configKey]: selectedId,
            });
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}

function SourceTypeSelectPanel({ handleClose }: { handleClose: () => void }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const handleToggle = (sourceType: string) => {
    const current = values.sourceTypeFilter;
    if (current.includes(sourceType)) {
      setFieldValue(
        "sourceTypeFilter",
        current.filter(s => s !== sourceType)
      );
    } else {
      setFieldValue("sourceTypeFilter", [...current, sourceType]);
    }
  };

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Source types" as any, { ns: "OrganisationSettings" })}
      />
      <ModalContent>
        <Stack spacing={0} overflow="auto">
          {SOURCE_TYPES.map(st => (
            <FormControlLabel
              key={st}
              control={
                <Checkbox
                  checked={values.sourceTypeFilter.includes(st)}
                  onChange={() => handleToggle(st)}
                  size="small"
                />
              }
              label={st}
            />
          ))}
        </Stack>
      </ModalContent>
      <DialogActions>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  );
}

function TriggerEventSelectPanel({
  handleClose,
  availableEvents,
  compatibleEvents,
}: {
  handleClose: () => void;
  availableEvents: string[];
  compatibleEvents: string[];
}) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const filteredEvents = React.useMemo(
    () => availableEvents.filter(e => compatibleEvents.includes(e)),
    [availableEvents, compatibleEvents]
  );

  const handleToggle = (eventName: string) => {
    const current = values.triggerEventNames;
    if (current.includes(eventName)) {
      setFieldValue(
        "triggerEventNames",
        current.filter(e => e !== eventName)
      );
    } else {
      setFieldValue("triggerEventNames", [...current, eventName]);
    }
  };

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Trigger events" as any, { ns: "OrganisationSettings" })}
      />
      <ModalContent>
        <Stack spacing={0} overflow="auto">
          {filteredEvents.map(eventName => (
            <FormControlLabel
              key={eventName}
              control={
                <Checkbox
                  checked={values.triggerEventNames.includes(eventName)}
                  onChange={() => handleToggle(eventName)}
                  size="small"
                />
              }
              label={eventName}
            />
          ))}
        </Stack>
      </ModalContent>
      <DialogActions>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  );
}

function RecipientSelectSidePanel({
  handleClose,
  configKey,
}: {
  handleClose: () => void;
  configKey: string;
}) {
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationProfile",
    "Global",
  ]);

  const { values, setFieldValue } = useFormikContext<FormValues>();
  const client = useApolloClient();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { getOrganisationRoleTitle } = useOrganisationRoles();

  const query = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      search: searchTerm || undefined,
    },
  });

  const members = React.useMemo(
    () =>
      (query.data ?? query.previousData)?.organisationMemberships.edges.map(
        edge => edge.node
      ) ?? [],
    [query.data, query.previousData]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "fullname",
        headerName: t("Name", { ns: "Global" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <UserAvatar userAvatar={row} size="s" />
            <Typography>{row.fullname}</Typography>
          </Stack>
        ),
      },
      {
        field: "roles",
        headerName: t("Roles", { ns: "OrganisationProfile" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) =>
          getOrganisationRoleTitle(row.roles),
      },
      {
        field: "email",
        headerName: t("Email", { ns: "Global" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) => row.email,
      },
    ],
    [t, getOrganisationRoleTitle]
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select recipient" as any, {
          ns: "OrganisationSettings",
        })}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={members}
              loading={query.loading}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("config", {
              ...values.config,
              [configKey]: selectedId,
            });
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}
